/* eslint-disable import/no-cycle */
import { useConvertCurrency } from '@utils/currencyConverter';
import { normaliseShopifyPrice } from '@utils/prices';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDelegateCash } from '../components/Web3/Web3Provider/useDelegateCash';
import { useWeb3Cache } from '../components/Web3/Web3Provider/useWeb3Cache';
import { AML_LIMIT } from '../constants/product';
import { useAuth } from '../providers';
import {
  AuthErrorResponse,
  AuthGateway,
  GenerateMultipassTokenResponse,
} from '../types/Auth';
import { ProductGateway } from '../types/AvantArteProduct';
import {
  GeneratedTaxType,
  useCalculateOrderPricingLazyQuery,
} from '../types/generated';
import { GiftCardCheckoutPayload } from '../types/GiftCard';
import { Frame, FrameOption } from '../types/Products';
import type {
  AttributeInput,
  CheckoutGateway,
  ProductBasketPurchasingController,
  ProductPurchaseDetails,
  ProductsGateway,
  ShopifyProductToCheckout,
} from '../types/Purchasing';
import { captureError } from '../utils/capture';
import { shopifyGraphqlIdToAdminId } from '../utils/shopifyIds';
import { useCountryCode } from './useCountryCode';
import { useCurrency } from './useCurrency';

export type AddToBasketArguments<TProductIdentifier> = {
  productId: TProductIdentifier;
  quantity: number;
  unlockCode?: string;
  customProperties?: AttributeInput[];
  framingOptionsToLoad?: Array<FrameOption>;
};

export type Options<
  TProductIdentifier,
  TProduct extends ProductPurchaseDetails,
> = {
  release?: string;
  productsGateway: ProductsGateway<TProductIdentifier, TProduct>;
  checkoutGateway: CheckoutGateway<TProduct, GiftCardCheckoutPayload>;
  aaProductsGateway: ProductGateway;
  authGateway: AuthGateway;
  initialProductToAddToBasket?: AddToBasketArguments<TProductIdentifier>;
  privateLinkIdentifier?: string;
  variantIdentifier?: string;
};

export function useProductBasketPurchasing<
  TProductIdentifier,
  TProduct extends ProductPurchaseDetails,
>(
  options: Options<TProductIdentifier, TProduct>,
): ProductBasketPurchasingController<TProductIdentifier> {
  const [currency] = useCurrency();
  const [countryCode] = useCountryCode();
  const { isLoggedIn } = useAuth();
  const { walletAddress } = useWeb3Cache();
  const delegateCash = useDelegateCash();
  const [productBasket, setProductBasket] = useState<
    Record<string, ShopifyProductToCheckout>
  >({});
  const [shopifyProductCache, setShopifyProductCache] = useState<
    Record<string, ProductPurchaseDetails | null>
  >({});
  const [frameBasket, setFrameBasket] = useState<Record<string, Frame>>({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const router = useRouter();
  const [currencyCode] = useCurrency();

  const basketToCheckout: ShopifyProductToCheckout[] = useMemo(
    () => [
      ...Object.values(productBasket),
      ...Object.entries(frameBasket).map(([key, frameBasketEntry]) => {
        const product = Object.values(productBasket).find(
          (value) => value.frameBasketEntryId === key,
        );

        return {
          product: {
            shopifyVariantId:
              frameBasketEntry.variant.consolidatedVariants[0]
                .shopifyFrameVariantId,
            price: frameBasketEntry.variant.consolidatedVariants[0].price,
            currency: currencyCode,
            noTaxCharged: false,
            productId:
              frameBasketEntry.variant.consolidatedVariants[0].shopifyFrameId,
            productTitle: frameBasketEntry.title,
            hasResaleAgreement: false,
          },
          quantity: product?.quantity || 1,
        };
      }),
    ],
    [frameBasket, productBasket],
  );

  const basketForOrderCalc = useMemo(() => {
    return {
      items: basketToCheckout.map((lineItem) => ({
        quantity: lineItem.quantity,
        variantId: lineItem.product.shopifyVariantId,
      })),
    };
  }, [basketToCheckout]);

  const [calculateOrderPricing] = useCalculateOrderPricingLazyQuery();

  const currencyConverter = useConvertCurrency();

  const getProductPurchaseDetailsWithCache = useCallback(
    (
      productId: TProductIdentifier,
      cacheEnabled: boolean = true,
    ): Promise<ProductPurchaseDetails | null> => {
      const cacheKey = `${
        (productId as unknown as ShopifyProductIdentifier).id
      }`;
      const shopifyProductFromCache = shopifyProductCache[cacheKey];

      if (shopifyProductFromCache && cacheEnabled) {
        return Promise.resolve(shopifyProductFromCache);
      }

      return new Promise((resolve) => {
        options.productsGateway
          .getProductPurchaseDetails({
            identifier: productId,
            releaseName: options.release || null,
            variantIdentifier: options.variantIdentifier,
          })
          .then((response) => {
            setShopifyProductCache((currentShopifyProductCache) => ({
              ...currentShopifyProductCache,
              [cacheKey]: response,
            }));
            resolve(response);
          })
          .catch((getProductError: Error) => {
            captureError(getProductError);
            resolve(null);
          });
      });
    },
    [
      options.productsGateway,
      options.release,
      options.variantIdentifier,
      shopifyProductCache,
    ],
  );

  const addProductToBasket = useCallback(
    ({
      customProperties,
      productId,
      quantity,
      unlockCode,
    }: AddToBasketArguments<TProductIdentifier>) => {
      getProductPurchaseDetailsWithCache(productId).then(
        (nextPurchasingDetails) => {
          if (nextPurchasingDetails && nextPurchasingDetails.shopifyVariantId) {
            setProductBasket((currentBasket) => ({
              ...currentBasket,
              [nextPurchasingDetails.shopifyVariantId!]: {
                product: {
                  productId: nextPurchasingDetails.shopifyId,
                  productTitle: nextPurchasingDetails.name,
                  shopifyVariantId: nextPurchasingDetails.shopifyVariantId!,
                  price: nextPurchasingDetails.price,
                  currency: nextPurchasingDetails.currency,
                  hasResaleAgreement: !nextPurchasingDetails.noResaleAgreement,
                  noTaxCharged: Boolean(nextPurchasingDetails.noTaxCharged),
                },
                quantity,
                unlockCode,
                customProperties,
              },
            }));
          }
        },
      );
    },
    [getProductPurchaseDetailsWithCache],
  );

  useEffect(() => {
    if (options.initialProductToAddToBasket) {
      addProductToBasket(options.initialProductToAddToBasket);
    }
  }, []);

  const addFrameToProduct = (
    productBasketEntryId: string,
    shopifyFrameProduct: Frame,
  ) => {
    if (!productBasket[productBasketEntryId]) return; // no product to add frame to
    const newFrameBasketEntryId = new Date().getTime().toString();

    setFrameBasket((currentFrameBasket) => ({
      ...currentFrameBasket,
      [newFrameBasketEntryId]: shopifyFrameProduct,
    }));

    setProductBasket((currentBasket) => ({
      ...currentBasket,
      [productBasketEntryId]: {
        ...currentBasket[productBasketEntryId],
        frameBasketEntryId: newFrameBasketEntryId,
        customProperties: [
          ...(currentBasket[productBasketEntryId].customProperties || []),
          {
            key: 'frame',
            value:
              shopifyFrameProduct.variant.consolidatedVariants[0]
                .shopifyFrameVariantId,
          },
        ],
      },
    }));
  };

  const updateProductQuantity = (
    productBasketEntryId: string,
    quantity: number,
  ) => {
    const productToCheckout = productBasket[productBasketEntryId];
    if (!productToCheckout) return; // no product to update

    setProductBasket((currentBasket) => ({
      ...currentBasket,
      [productBasketEntryId]: {
        ...productToCheckout,
        quantity,
      },
    }));
  };

  const removeProductFromBasket = (productBasketEntryId: string) => {
    const { frameBasketEntryId } = productBasket[productBasketEntryId];

    setProductBasket((currentBasket) =>
      Object.entries(currentBasket)
        .filter(([key]) => {
          return key !== productBasketEntryId;
        })
        .reduce(
          (prouctBasket, [key, value]) => ({
            ...prouctBasket,
            [key]: value,
          }),
          {},
        ),
    );

    if (frameBasketEntryId) {
      setFrameBasket((currentFrameBasket) =>
        Object.entries(currentFrameBasket)
          .filter(([key]) => {
            return key !== frameBasketEntryId;
          })
          .reduce(
            (frameBaskets, [key, value]) => ({
              ...frameBaskets,
              [key]: value,
            }),
            {},
          ),
      );
    }
  };

  const removeFrameFromProduct = (productBasketEntryId: string) => {
    const productToCheckout = productBasket[productBasketEntryId];
    if (!productToCheckout) return; // no product to add frame to
    if (!productToCheckout.frameBasketEntryId) return; // product doesn't have frame

    setFrameBasket((currentFrameBasket) =>
      Object.entries(currentFrameBasket)
        .filter(([key]) => {
          return key !== productToCheckout.frameBasketEntryId;
        })
        .reduce(
          (frameBaskets, [key, value]) => ({
            ...frameBaskets,
            [key]: value,
          }),
          {},
        ),
    );

    setProductBasket((currentBasket) => ({
      ...currentBasket,
      [productBasketEntryId]: {
        ...productToCheckout,
        frameBasketEntryId: undefined,
        customProperties: (productToCheckout.customProperties || []).filter(
          (customAttr) => customAttr.key !== 'frame',
        ),
      },
    }));
  };

  const getUserInformationAttributes = () => {
    const attributes: AttributeInput[] = [];
    if (isLoggedIn) {
      attributes.push({
        key: 'user_state',
        value: 'LOGGED_IN',
      });
    } else {
      attributes.push({
        key: 'user_state',
        value: 'LOGGED_OUT',
      });
    }
    return attributes;
  };

  const getTaxInformationAttributes = async () => {
    const attributes: AttributeInput[] = [];
    const taxReponse =
      await options.aaProductsGateway.getTaxesForCountryCode(countryCode);

    attributes.push({ key: 'country_code', value: countryCode });
    if (taxReponse.tax && taxReponse.tax.taxType === GeneratedTaxType.Vat) {
      attributes.push({
        key: 'vat_rate',
        value: taxReponse.tax.vatRate.toString(),
      });
    }

    return attributes;
  };

  const calculateBasketPricing = useCallback(async () => {
    const response = await calculateOrderPricing({
      variables: { items: basketForOrderCalc.items, countryCode, currencyCode },
    });
    return response.data?.calculateOrderPricing;
  }, [basketForOrderCalc, countryCode, currencyCode, calculateOrderPricing]);

  const goToCheckout = async (customAttributes?: AttributeInput[]) => {
    setCheckoutLoading(true);

    const checkoutCustomAttributes: AttributeInput[] = [];
    if (customAttributes) {
      checkoutCustomAttributes.push(...customAttributes);
    }

    if (options.privateLinkIdentifier) {
      checkoutCustomAttributes.push({
        'key': 'private_link_id',
        'value': options.privateLinkIdentifier,
      });
    }

    // user information
    checkoutCustomAttributes.push(...getUserInformationAttributes());
    // tax information
    const taxAttributes = await getTaxInformationAttributes();
    checkoutCustomAttributes.push(...taxAttributes);

    // we add the avant pass token if the user is logged in
    if (isLoggedIn) {
      try {
        checkoutCustomAttributes.push(
          (
            await options.authGateway.generateAvantPassToken({
              walletAddress,
              delegateCashVault: delegateCash.activeVaultAddress,
            })
          ).customAttribute,
        );
      } catch (e) {
        console.error(e);
      }
    }

    const result = await options.checkoutGateway.checkoutWithListOfProducts(
      basketToCheckout,
      currency,
      checkoutCustomAttributes,
    );

    let redirectURL = result;

    if (isLoggedIn) {
      const multipassTokenResponse =
        await options.authGateway.generateShopifyMultipassToken(result);

      if (!(multipassTokenResponse as AuthErrorResponse).error) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        redirectURL = `${process.env.NEXT_PUBLIC_MULTIPASS_URL}${
          (multipassTokenResponse as GenerateMultipassTokenResponse).token
        }`;
      }
    }

    const amlLimit = currencyConverter.convert(AML_LIMIT, currency);
    const response = await calculateBasketPricing();
    const total = response?.totalPrice;
    if (total && normaliseShopifyPrice(total, currency) > amlLimit) {
      const searchParams = new URLSearchParams({
        redirect: redirectURL,
      });
      router.push({ pathname: '/aml', search: `?${searchParams.toString()}` });
    } else {
      router.push(redirectURL);
    }
  };

  const findEntryIdByProductId = (productId: TProductIdentifier) => {
    return Object.keys(productBasket).find((entryId) => {
      return (
        shopifyGraphqlIdToAdminId(productBasket[entryId].product.productId) ===
        `${(productId as unknown as ShopifyProductIdentifier).id}`
      );
    });
  };

  return {
    checkoutLoading,
    productBasket,
    frameBasket,
    calculateBasketPricing,
    addFrameToProduct,
    addProductToBasket,
    goToCheckout,
    removeFrameFromProduct,
    removeProductFromBasket,
    updateProductQuantity,
    findEntryIdByProductId,
  };
}
